import { useEffect, useRef } from 'react'
import { motion, useMotionValue, useSpring, useAnimationFrame } from 'framer-motion'
import { ANIMATION_DRIFT_SPEED, ANIMATION_MIN_SCALE, ANIMATION_SCALE_DIFF } from '../../config/constants'

type Props = {
    index: number
    url: string
    initialX: number
    initialY: number
    onOffscreen?: (index: number) => void
}

const AnimatedImage = ({ url, initialX, initialY, onOffscreen, index }: Props) => {
    const drift = useRef((Math.random() - Math.random()) * ANIMATION_DRIFT_SPEED).current
    const scaleTarget = useRef(ANIMATION_MIN_SCALE + ANIMATION_SCALE_DIFF * Math.random()).current
    const velocity = useRef(3 * scaleTarget).current

    const xRaw = useMotionValue(initialX)
    const yRaw = useMotionValue(initialY)
    const rotateRaw = useMotionValue((Math.random() - Math.random()) * 15)
    const scaleRaw = useMotionValue(0)

    const x = useSpring(xRaw, { damping: 20, stiffness: 40 })
    const y = useSpring(yRaw, { damping: 20, stiffness: 40 })
    const rotate = useSpring(rotateRaw, { damping: 20, stiffness: 40 })
    const scale = useSpring(scaleRaw, { damping: 20, stiffness: 40 })

    const hasFired = useRef(false)

    useEffect(() => {
        scaleRaw.set(scaleTarget)
        /**
         * We want to trigger scale-in only once on mount
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useAnimationFrame(() => {
        xRaw.set(xRaw.get() + drift * scaleTarget)
        yRaw.set(yRaw.get() - velocity)
        rotateRaw.set(rotateRaw.get() + drift * scaleTarget)

        if (!hasFired.current && yRaw.get() < -500) {
            hasFired.current = true
            onOffscreen?.(index)
        }
    })

    return (
        <motion.div className="absolute -mt-[150px] -ml-[100px] h-[200px] w-[300px]" style={{ x, y, rotate, scale }}>
            <motion.img src={url} alt="" className="h-full w-full object-contain" style={{ scale: 1 }} />
        </motion.div>
    )
}

export default AnimatedImage
